import { useState, useEffect } from 'react';
import { palette, fontFamily, admin, sidebar as sb } from '../styles/tokens';
import { useAuthStore } from '../stores/authStore';
import { Logo } from '../components/Logo';

// Session 168 §Auth.OAuthSignupPlacesAutocomplete Phase D.1 — multi-color G.
function GoogleGLogo({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
      <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  );
}

export function LoginPage() {
  useEffect(() => { document.title = 'CrewBit · Sign In'; }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const signIn = useAuthStore(s => s.signIn);
  const signInWithGoogle = useAuthStore(s => s.signInWithGoogle);
  const loading = useAuthStore(s => s.loading);
  const error = useAuthStore(s => s.error);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void signIn(email, password);
  };

  const handleGoogle = () => { void signInWithGoogle(); };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      fontFamily,
    }}>
      <style>{`
        .crewbit-login-input:focus-visible {
          border-color: ${palette.accent};
          outline: 2px solid ${palette.accent};
          outline-offset: -1px;
        }
      `}</style>
      {/* Left panel — dark preview */}
      <div style={{
        width: 400, background: sb.bg, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: 40,
      }}>
        <Logo variant="stacked" size="lg" colorScheme="white" />
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 16, textAlign: 'center', lineHeight: 1.5 }}>
          Your desktop command center.
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: admin.bg,
      }}>
        <div style={{ width: 360 }}>
          <div style={{ fontSize: 22, fontWeight: 600, color: admin.header, letterSpacing: '-0.02em', marginBottom: 4 }}>
            Sign in
          </div>
          <div style={{ fontSize: 13, color: admin.caption, marginBottom: 28 }}>
            Use the same credentials as your CrewBit mobile app.
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              padding: '10px 0', borderRadius: 8,
              background: palette.white,
              color: admin.header, fontSize: 14, fontWeight: 600,
              border: `0.5px solid ${admin.border}`,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s', fontFamily: 'inherit',
              marginBottom: 16,
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = palette.g50; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = palette.white; }}
          >
            <GoogleGLogo />
            <span>Continue with Google</span>
          </button>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
          }}>
            <div style={{ flex: 1, height: 1, background: admin.border }} />
            <span style={{ fontSize: 11, color: admin.caption, letterSpacing: 0.3, textTransform: 'uppercase' }}>or</span>
            <div style={{ flex: 1, height: 1, background: admin.border }} />
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{
                padding: '10px 12px', borderRadius: 8,
                background: palette.redWash, color: palette.red,
                fontSize: 13, fontWeight: 500, marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: admin.body, marginBottom: 6 }}>Email</label>
              <input
                type="email"
                className="crewbit-login-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: `0.5px solid ${admin.border}`, fontSize: 14, color: admin.header,
                  outline: 'none', transition: 'border-color 0.15s',
                  fontFamily: 'inherit', boxSizing: 'border-box',
                  background: palette.white,
                }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: admin.body, marginBottom: 6 }}>Password</label>
              <input
                type="password"
                className="crewbit-login-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: `0.5px solid ${admin.border}`, fontSize: 14, color: admin.header,
                  outline: 'none', transition: 'border-color 0.15s',
                  fontFamily: 'inherit', boxSizing: 'border-box',
                  background: palette.white,
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '10px 0', borderRadius: 8,
                background: loading ? palette.g300 : palette.accent,
                color: palette.white, fontSize: 14, fontWeight: 600,
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s', fontFamily: 'inherit',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = palette.accentDeep; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = palette.accent; }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
